import { useMemo, useState } from 'react';
import {
  FaCheck,
  FaStar,
  FaArrowLeft,
  FaScaleUnbalanced,
} from 'react-icons/fa6';
import { MdCheckCircle, MdOutlineLightbulb } from 'react-icons/md';
import { Job } from '../../../../utils/type';
import { formatCurrency } from '../../../../utils/currency';
import {
  getSiblingEffectiveState,
  QuoteRequest,
} from '../../../../utils/quoteFlowRequests';
import ProwlLine from '../../quote-flow/ProwlLine';
import QuoteColumn from '../../quote-flow/QuoteColumn';
import { formatDate } from '../../../../utils/jobDetail.helpers';

export const providerLabel = (job: Job): string =>
  job.businessName || job.providerName || job.provider?.name || 'Provider';

const GAP_LABELS: Record<keyof NonNullable<Job['quoteCompleteness']>, string> =
  {
    itemised: 'An itemised price breakdown',
    hasInclusions: "What's included",
    hasExclusions: "What's excluded",
    hasGuarantee: 'A guarantee',
    hasEarliestStart: 'An earliest start date',
    gapCount: '',
  };

const deriveGaps = (job: Job): string[] => {
  const c = job.quoteCompleteness;
  if (!c) return [];
  const gaps: string[] = [];
  if (!c.itemised) gaps.push(GAP_LABELS.itemised);
  if (!c.hasInclusions) gaps.push(GAP_LABELS.hasInclusions);
  if (!c.hasExclusions) gaps.push(GAP_LABELS.hasExclusions);
  if (!c.hasGuarantee) gaps.push(GAP_LABELS.hasGuarantee);
  if (!c.hasEarliestStart) gaps.push(GAP_LABELS.hasEarliestStart);
  return gaps;
};

interface ExclusionCrossReference {
  fromProvider: string;
  toProvider: string;
  term: string;
  price?: number;
}

const findExclusionCrossReferences = (
  jobs: Job[]
): ExclusionCrossReference[] => {
  const matches: ExclusionCrossReference[] = [];
  for (const from of jobs) {
    for (const term of from.exclusions ?? []) {
      const needle = term.trim().toLowerCase();
      if (!needle) continue;
      const to = jobs.find(
        (candidate) =>
          candidate !== from &&
          candidate.lineItems?.some((li) =>
            li.description.toLowerCase().includes(needle)
          )
      );
      if (to) {
        const li = to.lineItems?.find((item) =>
          item.description.toLowerCase().includes(needle)
        );
        matches.push({
          fromProvider: providerLabel(from),
          toProvider: providerLabel(to),
          term,
          price: typeof li?.lineTotal === 'number' ? li.lineTotal : undefined,
        });
      }
    }
  }
  return matches;
};

const buildProwlRead = (jobs: Job[]): string | null => {
  if (jobs.length < 2) return null;
  const parts: string[] = [];
  const prices = jobs
    .map((j) => j.quotedPrice)
    .filter((p): p is number => typeof p === 'number');

  if (prices.length >= 2) {
    const min = Math.min(...prices);
    const max = Math.max(...prices);
    if (max > min) {
      parts.push(
        `Quotes range from ${formatCurrency(min)} to ${formatCurrency(max)} — ${formatCurrency(max - min)} difference.`
      );
    }
  }

  const completeOnes = jobs.filter((j) => j.quoteCompleteness?.gapCount === 0);
  const hasIncompleteOnes = jobs.some(
    (j) => (j.quoteCompleteness?.gapCount ?? 0) > 0
  );

  if (completeOnes.length === 1 && hasIncompleteOnes) {
    parts.push(
      `${providerLabel(completeOnes[0])} is the only quote with no missing information.`
    );
  }

  const [match] = findExclusionCrossReferences(jobs);
  if (match) {
    parts.push(
      `${match.fromProvider} excludes ${match.term}. ${match.toProvider} lists ${match.term}${
        typeof match.price === 'number'
          ? ` at ${formatCurrency(match.price)}`
          : ''
      }.`
    );
  }

  return parts.length > 0 ? parts.join(' ') : null;
};

const S8_QUOTES_TO_COMPARE = 5;

const QuoteComparisonPanel = ({
  request,
  onAskQuestion,
  onAccept,
  isAccepting,
  allQuotesReceived = true,
  onBackToTracking,
}: {
  request: QuoteRequest;
  onAskQuestion: (job: Job) => void;
  onAccept: (job: Job) => void;
  isAccepting: boolean;
  /** False when the user has jumped into comparison before every provider replied. */
  allQuotesReceived?: boolean;
  /** Shown as a "back to tracking" control when comparing early (allQuotesReceived is false). */
  onBackToTracking?: () => void;
}) => {
  const liveJobs = useMemo(
    () =>
      request.jobs
        .filter((j) => getSiblingEffectiveState(j) === 'quoted-live')
        .sort((a, b) => (a.quotedAt ?? '').localeCompare(b.quotedAt ?? '')),
    [request.jobs]
  );

  const respondedCount = request.jobs.filter(
    (j) => getSiblingEffectiveState(j) !== 'requested'
  ).length;

  const stillToReply = request.jobs.length - respondedCount;
  const manyQuotes = liveJobs.length > S8_QUOTES_TO_COMPARE;

  const [picked, setPicked] = useState<string[]>(() =>
    liveJobs.slice(0, S8_QUOTES_TO_COMPARE).map((j) => j.jobId)
  );

  const validPicked = picked.filter((id) =>
    liveJobs.some((j) => j.jobId === id)
  );

  const effectivePicked =
    manyQuotes && validPicked.length < S8_QUOTES_TO_COMPARE
      ? [
          ...validPicked,
          ...liveJobs
            .filter((j) => !validPicked.includes(j.jobId))
            .slice(0, S8_QUOTES_TO_COMPARE - validPicked.length)
            .map((j) => j.jobId),
        ]
      : validPicked;

  const visibleJobs = manyQuotes
    ? liveJobs.filter((j) => effectivePicked.includes(j.jobId))
    : liveJobs;

  const collapsedJobs = manyQuotes
    ? liveJobs.filter((j) => !effectivePicked.includes(j.jobId))
    : [];

  // Collect ALL job IDs that share the lowest price so every tied card gets the chip.
  const lowestPriceJobIds = useMemo(() => {
    if (visibleJobs.length < 2) return new Set<string>();
    let minPrice = Infinity;
    visibleJobs.forEach((j) => {
      if (typeof j.total === 'number' && j.total < minPrice) {
        minPrice = j.total;
      }
    });
    if (!isFinite(minPrice)) return new Set<string>();
    return new Set(
      visibleJobs.filter((j) => j.total === minPrice).map((j) => j.jobId)
    );
  }, [visibleJobs]);

  const toggle = (jobId: string) => {
    const isPicked = effectivePicked.includes(jobId);
    if (isPicked) {
      setPicked(effectivePicked.filter((id) => id !== jobId));
    } else if (effectivePicked.length < S8_QUOTES_TO_COMPARE) {
      setPicked([...effectivePicked, jobId]);
    } else {
      setPicked([jobId, ...effectivePicked.slice(0, 2)]);
    }
  };

  const [prowlDismissed, setProwlDismissed] = useState(false);

  const prowlText = useMemo(
    () => buildProwlRead(visibleJobs),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [visibleJobs.map((j) => j.jobId).join(',')]
  );

  return (
    <div className="space-y-6 w-full">
      {/* Early-comparison Header Notice */}
      {!allQuotesReceived && (
        <div className="flex items-center justify-between flex-wrap gap-3 bg-amber-50/80 border border-amber-200/80 text-amber-900 rounded-2xl p-4 shadow-2xs">
          <div className="flex items-center gap-2.5 text-xs font-bold">
            <span className="w-2.5 h-2.5 rounded-full bg-amber-500 animate-pulse shrink-0" />
            <span>
              Comparing {liveJobs.length} quote
              {liveJobs.length === 1 ? '' : 's'} so far
              {stillToReply > 0
                ? ` · ${stillToReply} more provider${stillToReply === 1 ? '' : 's'} still replying.`
                : '.'}
            </span>
          </div>
          {onBackToTracking && (
            <button
              type="button"
              onClick={onBackToTracking}
              className="flex items-center gap-1.5 text-xs font-bold text-amber-800 hover:text-amber-950 transition-colors bg-white/80 px-3 py-1.5 rounded-xl border border-amber-200 shadow-2xs"
            >
              <FaArrowLeft size={10} /> Back to tracking
            </button>
          )}
        </div>
      )}

      {/* Smart Prowl Comparison Insights */}
      {prowlText && !prowlDismissed && (
        <div>
          <ProwlLine
            text={prowlText}
            variant="boxed"
            onDismiss={() => setProwlDismissed(true)}
          />
        </div>
      )}

      {prowlText && prowlDismissed && (
        <button
          type="button"
          onClick={() => setProwlDismissed(false)}
          className="text-xs text-primary font-bold flex items-center gap-1.5 hover:underline px-1"
        >
          <MdOutlineLightbulb className="text-amber-500" size={16} />
          Show Prowl Smart Comparison Insights
        </button>
      )}

      {/* Multi-Quote Column Toggles */}
      {manyQuotes && (
        <div className="bg-white p-4 rounded-2xl border border-slate-200/80 shadow-2xs flex flex-wrap items-center gap-2.5">
          <div className="flex items-center gap-1.5 text-slate-400 mr-1">
            <FaScaleUnbalanced size={14} />
            <span className="text-xs font-bold uppercase tracking-wider">
              Active Columns:
            </span>
          </div>
          {liveJobs.map((j) => {
            const isPicked = effectivePicked.includes(j.jobId);
            return (
              <button
                key={j.jobId}
                type="button"
                onClick={() => toggle(j.jobId)}
                className={`text-xs px-3.5 py-1.5 rounded-xl border transition-all flex items-center gap-1.5 ${
                  isPicked
                    ? 'border-primary bg-primary/10 text-primary font-bold shadow-2xs'
                    : 'border-slate-200/80 bg-slate-50 text-slate-600 hover:border-slate-300'
                }`}
              >
                {isPicked && <FaCheck size={11} />}
                {providerLabel(j)}
              </button>
            );
          })}
          <span className="text-xs font-semibold text-slate-400 ml-auto">
            {effectivePicked.length}/{liveJobs.length} displayed
          </span>
        </div>
      )}

      {/* Grid Matrix View — Updated for 5 Grid Columns across full width */}
      <div
        className={`grid gap-4 ${
          visibleJobs.length <= 1
            ? 'grid-cols-1'
            : visibleJobs.length === 2
              ? 'grid-cols-1 sm:grid-cols-2'
              : visibleJobs.length === 3
                ? 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-3'
                : visibleJobs.length === 4
                  ? 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-4'
                  : 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-5'
        }`}
      >
        {visibleJobs.map((job) => {
          const itemised = (job.lineItems?.length ?? 0) > 0;
          const isLowest = lowestPriceJobIds.has(job.jobId);
          const isComplete = job.quoteCompleteness?.gapCount === 0;

          return (
            <div key={job.jobId} className="relative group">
              {/* Badges for Best Value / Most Complete */}
              <div className="absolute -top-3 left-4 z-10 flex gap-1.5">
                {isLowest && (
                  <span className="bg-emerald-600 text-white text-[10px] font-black uppercase tracking-wider px-2.5 py-0.5 rounded-full shadow-sm flex items-center gap-1">
                    <FaStar size={10} /> Lowest Price
                  </span>
                )}
                {isComplete && !isLowest && (
                  <span className="bg-primary text-white text-[10px] font-black uppercase tracking-wider px-2.5 py-0.5 rounded-full shadow-sm flex items-center gap-1">
                    <MdCheckCircle size={12} /> Complete Quote
                  </span>
                )}
              </div>

              <QuoteColumn
                providerName={providerLabel(job)}
                quotedAtLabel={
                  job.quotedAt ? `Quoted ${formatDate(job.quotedAt)}` : ''
                }
                total={job.total ?? 0}
                totalNote={
                  typeof job.estimatedDurationDays === 'number'
                    ? `${job.estimatedDurationDays} day${
                        job.estimatedDurationDays === 1 ? '' : 's'
                      } estimated`
                    : undefined
                }
                lineItems={
                  itemised
                    ? job.lineItems!.map((li) => ({
                        label: li.description,
                        sub: `${li.quantity} × ${formatCurrency(li.unitPrice)}`,
                        amount:
                          typeof li.lineTotal === 'number'
                            ? li.lineTotal
                            : undefined,
                      }))
                    : []
                }
                included={job.inclusions ?? []}
                excluded={job.exclusions ?? []}
                gaps={deriveGaps(job)}
                onAccept={() => onAccept(job)}
                onAskQuestion={() => onAskQuestion(job)}
                /* ── NEW: price breakdown data ── */
                addonSubtotal={job.addonSubtotal || 0}
                additionalServices={
                  Array.isArray(job.additionalServices)
                    ? job.additionalServices.map((s) => ({
                        id: s.id,
                        serviceItem: s.serviceItem,
                        price: s.price,
                      }))
                    : []
                }
                adjustment={job.adjustment}
                quotedPrice={job.quotedPrice}
                className={`transition-all rounded-2xl bg-white border ${
                  isLowest
                    ? 'border-emerald-400/80 shadow-md ring-2 ring-emerald-500/10'
                    : 'border-slate-200/80 hover:border-slate-300 shadow-sm'
                } ${isAccepting ? 'opacity-60 pointer-events-none' : ''}`}
              />
            </div>
          );
        })}
      </div>

      {/* Overflow Collapsed Quotes Row */}
      {collapsedJobs.length > 0 && (
        <div className="space-y-3 pt-2">
          <p className="text-xs font-bold tracking-wider uppercase text-slate-400">
            Other Received Quotes ({collapsedJobs.length})
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {collapsedJobs.map((job) => {
              const gapCount = job.quoteCompleteness?.gapCount ?? 0;
              return (
                <div
                  key={job.jobId}
                  className="bg-white border border-slate-200/80 rounded-2xl px-4 py-3 flex items-center justify-between gap-3 shadow-2xs hover:border-slate-300 transition-all"
                >
                  <div className="min-w-0">
                    <p className="text-sm font-semibold text-slate-800 truncate">
                      {providerLabel(job)}
                    </p>
                    <p className="text-xs text-slate-400">
                      {gapCount === 0
                        ? 'No gaps'
                        : `${gapCount} details missing`}
                    </p>
                  </div>
                  <div className="flex items-center gap-3 shrink-0">
                    <span className="text-sm font-bold text-slate-900">
                      {formatCurrency(job.quotedPrice ?? 0)}
                    </span>
                    <button
                      type="button"
                      onClick={() => toggle(job.jobId)}
                      className="text-xs text-primary font-bold hover:underline bg-primary/10 px-2.5 py-1 rounded-lg"
                    >
                      Swap in
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

export default QuoteComparisonPanel;
